import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Button,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useSelector } from "react-redux";
import Header from "components/Header";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import forageImage from "../../assets/forage.png";
import api from "services/api";

const getCurrentDate = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

const getInitialFormData = () => ({
  selectedDate: getCurrentDate(),
  nombreMp: 0,
  dureePompage: 0,
  gazoilConsomme: 0,
  electriciteConsomme: 0,
  nombreJourPanne: 0,
  visiteMaintPrev: 0,
  vidangeMoteur: 0,
  nombreMpUnit: "m3",
  dureePompageUnit: "heures",
  gazoilConsommeUnit: "litres",
  electriciteConsommeUnit: "kWh",
  nombreJourPanneUnit: "jours",
  visiteMaintPrevUnit: "nombre",
  vidangeMoteurUnit: "nombre",
});

const DonneesProductions = () => {
  const mode = useSelector((state) => state.global.mode);

  const [formData, setFormData] = useState(getInitialFormData());
  const [missingMonths, setMissingMonths] = useState([]);

  useEffect(() => {
    fetchMissingMonths();
  }, []);

  const fetchMissingMonths = async () => {
    try {
      const response = await api.get("/api/productions/missing-months");
      setMissingMonths(response.data.missingMonths || []);
    } catch (error) {
      console.error("Erreur récupération mois manquants :", error);
    }
  };

  const handleMissingMonthSelect = (event) => {
    setFormData((prevData) => ({
      ...prevData,
      selectedDate: event.target.value,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]:
        name === "selectedDate" || name.includes("Unit")
          ? value
          : Number(value),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      const response = await api.post("/api/productions", formData);

      console.log("Form submitted:", response.data);

      toast.success("Formulaire soumis avec succès.");

      setFormData(getInitialFormData());
      fetchMissingMonths();
    } catch (error) {
      console.error("Erreur lors de la soumission du formulaire!", error);

      const backendMessage = error.response?.data?.message;

      if (backendMessage?.includes("mois précédent")) {
        toast.error(
          "Impossible de sauter un mois. Même si les valeurs sont à zéro, vous devez d’abord enregistrer la production du mois précédent."
        );
      } else if (backendMessage?.includes("mois en cours")) {
        toast.error(
          "Vous ne pouvez pas encore enregistrer la production du mois en cours ou d’un mois futur."
        );
      } else if (backendMessage?.includes("existe déjà")) {
        toast.error("Une production existe déjà pour ce mois.");
      } else {
        toast.error(
          backendMessage ||
            "Erreur lors de la soumission du formulaire. Veuillez réessayer."
        );
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, ml: 3 }}>
      <Header
        title="Ajouter une donnée de production"
        subtitle="Les valeurs numériques sont remplies par défaut à 0"
        sx={{ mb: 5 }}
      />

      <Grid container spacing={3} sx={{ mt: 4 }}>
        <Grid item xs={8}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Date"
                  type="date"
                  name="selectedDate"
                  value={formData.selectedDate}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& input::-webkit-calendar-picker-indicator": {
                      color: "darkgoldenrod",
                      filter:
                        "invert(0.5) sepia(1) saturate(10) hue-rotate(5deg) brightness(1.1) contrast(1.2)",
                    },
                  }}
                />
              </Grid>

              {[
                { field: "nombreMp", label: "Nombre MP" },
                { field: "dureePompage", label: "Durée de Pompage" },
                { field: "gazoilConsomme", label: "Gazoil Consommé" },
                { field: "electriciteConsomme", label: "Électricité Consommée" },
                { field: "nombreJourPanne", label: "Nombre de Jours de Panne" },
                { field: "visiteMaintPrev", label: "Visite Maintenance Préventive" },
                { field: "vidangeMoteur", label: "Vidange Moteur" },
              ].map(({ field, label }) => (
                <React.Fragment key={field}>
                  <Grid item xs={3}>
                    <TextField
                      label={label}
                      type="number"
                      name={field}
                      value={formData[field]}
                      onChange={handleChange}
                      required
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <TextField
                      label={`${label} Unit`}
                      name={`${field}Unit`}
                      value={formData[`${field}Unit`]}
                      onChange={handleChange}
                      fullWidth
                      disabled
                    />
                  </Grid>
                </React.Fragment>
              ))}

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color={mode === "dark" ? "secondary" : "primary"}
                  size="large"
                  sx={{ width: "100%" }}
                >
                  Ajouter
                </Button>
              </Grid>
            </Grid>
          </form>
        </Grid>

        <Grid
          item
          xs={4}
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Mois non enregistrés</InputLabel>
            <Select
              value=""
              label="Mois non enregistrés"
              onChange={handleMissingMonthSelect}
            >
              {missingMonths.length > 0 ? (
                missingMonths.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>Aucun mois manquant</MenuItem>
              )}
            </Select>
          </FormControl>

          <img
            src={forageImage}
            alt="Forage"
            style={{
              width: "100%",
              maxWidth: "200px",
              display: "block",
              margin: "0 auto",
            }}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default DonneesProductions;



// import React, { useState } from "react";
// import { Grid, TextField, Button, Container } from "@mui/material";
// import { useSelector } from "react-redux";
// import Header from "components/Header";
// import { toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import forageImage from "../../assets/forage.png";
// import api from "services/api";

// const getCurrentDate = () => {
//   const today = new Date();
//   return today.toISOString().split("T")[0];
// };

// const DonneesProductions = () => {
//   const mode = useSelector((state) => state.global.mode);

//   const [formData, setFormData] = useState({
//     selectedDate: getCurrentDate(),
//     nombreMp: 0,
//     dureePompage: 0,
//     gazoilConsomme: 0,
//     electriciteConsomme: 0,
//     nombreJourPanne: 0,
//     visiteMaintPrev: 0,
//     vidangeMoteur: 0,
//     nombreMpUnit: "m3",
//     dureePompageUnit: "heures",
//     gazoilConsommeUnit: "litres",
//     electriciteConsommeUnit: "kWh",
//     nombreJourPanneUnit: "jours",
//     visiteMaintPrevUnit: "nombre",
//     vidangeMoteurUnit: "nombre",
//   });

//   const handleChange = (e) => {
//     const { name, value } = e.target;

//     setFormData((prevData) => ({
//       ...prevData,
//       [name]:
//         name === "selectedDate" || name.includes("Unit")
//           ? value
//           : Number(value),
//     }));
//   };

//   const handleSubmit = async (event) => {
//     event.preventDefault();

//     try {
//       const response = await api.post(
//         "/api/productions",
//         formData
//       );

//       console.log("Form submitted:", response.data);

//       toast.success("Formulaire soumis avec succès.");

//       setFormData({
//         selectedDate: getCurrentDate(),
//         nombreMp: 0,
//         dureePompage: 0,
//         gazoilConsomme: 0,
//         electriciteConsomme: 0,
//         nombreJourPanne: 0,
//         visiteMaintPrev: 0,
//         vidangeMoteur: 0,
//         nombreMpUnit: "m3",
//         dureePompageUnit: "heures",
//         gazoilConsommeUnit: "litres",
//         electriciteConsommeUnit: "kWh",
//         nombreJourPanneUnit: "jours",
//         visiteMaintPrevUnit: "nombre",
//         vidangeMoteurUnit: "nombre",
//       });
//     } catch (error) {
//       console.error(
//         "Erreur lors de la soumission du formulaire!",
//         error
//       );

//       const backendMessage = error.response?.data?.message;

//       if (backendMessage?.includes("mois précédent")) {
//         toast.error(
//           "Impossible de sauter un mois. Même si les valeurs sont à zéro, vous devez d’abord enregistrer la production du mois précédent."
//         );
//       } else if (backendMessage?.includes("existe déjà")) {
//         toast.error("Une production existe déjà pour ce mois.");
//       } else {
//         toast.error(
//           backendMessage ||
//           "Erreur lors de la soumission du formulaire. Veuillez réessayer."
//         );
//       }
//     }
//   };

//   return (
//     <Container maxWidth="lg" sx={{ mt: 4, ml: 3 }}>
//       <Header
//         title="Ajouter une donnée de production"
//         subtitle="Les valeurs numériques sont remplies par défaut à 0"
//         sx={{ mb: 5 }}
//       />
//       <Grid container spacing={3} sx={{ mt: 4 }}>
//         <Grid item xs={8}>
//           <form onSubmit={handleSubmit}>
//             <Grid container spacing={2}>
//               <Grid item xs={6}>
//                 <TextField
//                   label="Date"
//                   type="date"
//                   name="selectedDate"
//                   value={formData.selectedDate}
//                   onChange={handleChange}
//                   required
//                   fullWidth
//                   InputLabelProps={{
//                     shrink: true,
//                   }}
//                   sx={{
//                     "& input::-webkit-calendar-picker-indicator": {
//                       color: "darkgoldenrod",
//                       filter: "invert(0.5) sepia(1) saturate(10) hue-rotate(5deg) brightness(1.1) contrast(1.2)",
//                     }
//                   }}
//                 />
//               </Grid>
//               {[
//                 { field: "nombreMp", label: "Nombre MP" },
//                 { field: "dureePompage", label: "Durée de Pompage" },
//                 { field: "gazoilConsomme", label: "Gazoil Consommé" },
//                 {
//                   field: "electriciteConsomme",
//                   label: "Électricité Consommée",
//                 },
//                 { field: "nombreJourPanne", label: "Nombre de Jours de Panne" },
//                 {
//                   field: "visiteMaintPrev",
//                   label: "Visite Maintenance Préventive",
//                 },
//                 { field: "vidangeMoteur", label: "Vidange Moteur" },
//               ].map(({ field, label }) => (
//                 <React.Fragment key={field}>
//                   <Grid item xs={3}>
//                     <TextField
//                       label={label}
//                       type="number"
//                       name={field}
//                       value={formData[field]}
//                       onChange={handleChange}
//                       required
//                       fullWidth
//                     />
//                   </Grid>
//                   <Grid item xs={3}>
//                     <TextField
//                       label={`${label} Unit`}
//                       name={`${field}Unit`}
//                       value={formData[`${field}Unit`]}
//                       onChange={handleChange}
//                       fullWidth
//                       disabled
//                     />
//                   </Grid>
//                 </React.Fragment>
//               ))}
//               <Grid item xs={12}>
//                 <Button
//                   type="submit"
//                   variant="contained"
//                   color={mode === "dark" ? "secondary" : "primary"}
//                   size="large"
//                   sx={{
//                     width: "100%",
//                   }}
//                 >
//                   Ajouter
//                 </Button>
//               </Grid>
//             </Grid>
//           </form>
//         </Grid>
//         <Grid item xs={4} container alignItems="center" justifyContent="center">
//           <img
//             src={forageImage}
//             alt="Forage"
//             style={{
//               width: "100%",
//               maxWidth: "200px",
//               display: "block",
//               margin: "0 auto",
//             }}
//           />
//         </Grid>
//       </Grid>
//     </Container>
//   );
// };

// export default DonneesProductions;
